import { useState, useEffect } from 'react'
import { useRealtimeAttendanceContext } from '@/modules/RealtimeAttendance/contexts/RealtimeAttendanceContext'
import useIsFirstRender from '@/hooks/useIsFirstRender'
import type {
  UseValidateRealtimeProps,
  UseValidateRealtimeReturn,
} from './types'

export const useValidateRealtime = (
  props: UseValidateRealtimeProps,
): UseValidateRealtimeReturn => {
  const {
    secondsToValidate = 5,
    onRealtimeWithoutAttendances,
    attendanceIdToValidate,
    onAttendanceIdNotFound,
  } = props

  const [timerStarted, setTimerStarted] = useState(false)
  const [hasValidated, setHasValidated] = useState(false)
  const [hasAttendances, setHasAttendances] = useState(false)
  const [hasSpecificAttendance, setHasSpecificAttendance] = useState(false)
  const { realtimeAttendance } = useRealtimeAttendanceContext()
  const isFirstRender = useIsFirstRender()

  const scheduledAttendances = realtimeAttendance?.scheduled

  useEffect(() => {
    if (isFirstRender) return

    if (!hasAttendances && !timerStarted) {
      setTimerStarted(true)

      setTimeout(() => {
        setTimerStarted(false)

        const hasScheduledAttendances = scheduledAttendances?.length > 0

        if (!hasScheduledAttendances) {
          onRealtimeWithoutAttendances?.()
        } else {
          setHasAttendances(true)
        }

        if (attendanceIdToValidate) {
          const isAttendanceOnRealtime = scheduledAttendances?.find(
            attendance => {
              return attendance?.id === attendanceIdToValidate
            },
          )

          if (!isAttendanceOnRealtime) {
            onAttendanceIdNotFound?.()
            return
          }

          setHasSpecificAttendance(true)
        }

        setHasValidated(true)
      }, secondsToValidate * 1000)
    }
  }, [
    isFirstRender,
    scheduledAttendances,
    hasAttendances,
    secondsToValidate,
    onRealtimeWithoutAttendances,
    attendanceIdToValidate,
    onAttendanceIdNotFound,
    timerStarted,
  ])

  useEffect(() => {
    if (isFirstRender || !hasAttendances) return

    const hasScheduledAttendances = scheduledAttendances?.length > 0

    if (!hasScheduledAttendances) {
      onRealtimeWithoutAttendances?.()
    }
  }, [
    isFirstRender,
    scheduledAttendances?.length,
    hasAttendances,
    onRealtimeWithoutAttendances,
  ])

  return {
    hasValidatedRealtime: hasValidated,
    hasRealtimeAttendances: hasAttendances,
    hasRealtimeSpecificAttendance: hasSpecificAttendance,
  }
}
